import type {
  GccDashboardData,
  GccDispositionTag,
  GccEndStateRole,
  GccOwnership,
  GccStage4Result,
  GccValueCase,
  GccValueFigures,
  GccWave,
} from '@shared/types'
import { gccWorkView } from './gccWorkView'
import { gccRoleEvolution } from './gccRoleEvolution'

/**
 * One card of the export's Agent Marketplace tab (WI 2822): a task AI acts on, in the
 * spirit of the original TN's Automation & AI Builds Catalog. `agentMatch` is only the
 * AI's estimate that an agent likely exists (WI 2050).
 */
export interface GccAgentBuild {
  taskName: string
  capabilityName: string
  functionScope: string
  disposition: Exclude<GccDispositionTag, 'Human'>
  /** FTE the AI lever releases; null on v1 designs, which have no task FTE */
  releasedFte: number | null
  /** Wave the AI lever lands in; null when the design has no sequencing for the task */
  wave: GccWave | null
  agentMatch: boolean
}

type GccTaskTagList = GccDashboardData['taggedTasks']['taggedTasks']

const round = (n: number, dp: number) => Math.round(n * 10 ** dp) / 10 ** dp

function byReleasedThenName(a: GccAgentBuild, b: GccAgentBuild): number {
  return (b.releasedFte ?? -1) - (a.releasedFte ?? -1) || a.taskName.localeCompare(b.taskName)
}

/**
 * The Agent Marketplace cards of a design: every task with an AI lever (not Human).
 * v2 designs take FTE, capability and wave from the value ledger; v1 designs fall back
 * to the Step 1 tags, the task register and Step 3 sequencing.
 */
export function gccAgentBuilds(gcc: Pick<GccDashboardData, 'taggedTasks' | 'taskRegister' | 'sequencing' | 'valueCase'>): GccAgentBuild[] {
  const tags: GccTaskTagList = gcc.taggedTasks?.taggedTasks ?? []
  const tagOf = (taskId: string, taskName: string) => tags.find(t => (t.taskId !== undefined && t.taskId === taskId) || t.taskName === taskName)
  const lines = gcc.valueCase?.tasks ?? []
  if (lines.length) {
    return lines
      .filter(l => l.disposition !== 'Human')
      .map(l => ({
        taskName: l.taskName,
        capabilityName: l.capabilityName,
        functionScope: l.functionScope,
        disposition: l.disposition as GccAgentBuild['disposition'],
        releasedFte: round(l.aiReleasedFte, 2),
        wave: l.transformWave,
        agentMatch: tagOf(l.taskId, l.taskName)?.agentMatch ?? false,
      }))
      .sort(byReleasedThenName)
  }
  const register = gcc.taskRegister?.taskRegister ?? []
  const sequenced = gcc.sequencing?.taskSequencing ?? []
  return tags
    .filter(t => t.disposition !== 'Human')
    .map(t => {
      const proc = register.find(p => p.tasks.some(task => (t.taskId !== undefined && task.taskId === t.taskId) || task.name === t.taskName))
      const released = t.baselineFte !== undefined && t.residualFte !== undefined ? round(Math.max(0, t.baselineFte - t.residualFte), 2) : null
      return {
        taskName: t.taskName,
        capabilityName: proc?.processName ?? '',
        functionScope: proc?.functionScope ?? '',
        disposition: t.disposition as GccAgentBuild['disposition'],
        releasedFte: released,
        wave: sequenced.find(s => s.taskId === t.taskId)?.transformWave ?? null,
        agentMatch: t.agentMatch,
      }
    })
    .sort(byReleasedThenName)
}

/** An end-state role as the Simple export embeds it: its saving, not the two labour costs. */
type SimpleRole = Omit<GccEndStateRole, 'labourCostTodayUSD' | 'labourCostEndStateUSD' | 'headcountDelta'> & { labourSavingUSD: number }

/** Value ledger lines merged per capability × ownership × disposition × waves: no task names, roles or rates. */
interface SimpleLedgerGroup extends GccValueFigures {
  capabilityName: string
  functionScope: string
  ownership: GccOwnership
  transformWave: GccWave | null
  migrationWave: GccWave | null
  baselineFte: number
  aiReleasedFte: number
  residualFte: number
}

function simpleRole(r: GccEndStateRole): SimpleRole {
  return {
    roleName: r.roleName,
    segment: r.segment,
    tag: r.tag,
    sourcingTier: r.sourcingTier,
    headcountToday: r.headcountToday,
    headcountEndState: r.headcountEndState,
    skillsIn: r.skillsIn,
    skillsOut: r.skillsOut,
    skillsNetNew: r.skillsNetNew,
    labourSavingUSD: Math.round((r.labourCostTodayUSD || 0) - (r.labourCostEndStateUSD || 0)),
  }
}

function simplePopulation(p: GccStage4Result | undefined) {
  if (!p) return p
  return {
    segment: p.segment,
    endStateRoles: (p.endStateRoles ?? []).map(simpleRole),
    totalHeadcountToday: p.totalHeadcountToday,
    totalHeadcountEndState: p.totalHeadcountEndState,
    totalLabourCostTodayUSD: p.totalLabourCostTodayUSD,
    totalLabourCostEndStateUSD: p.totalLabourCostEndStateUSD,
    labourSavingUSD: p.labourSavingUSD,
    aiSavingUSD: p.aiSavingUSD,
    locationSavingUSD: p.locationSavingUSD,
  }
}

const FIGURES = ['currentCostUSD', 'aiSavingUSD', 'locationSavingUSD', 'totalSavingUSD', 'endStateCostUSD'] as const
const FTES = ['baselineFte', 'aiReleasedFte', 'residualFte'] as const

/**
 * Merges the ledger so no line belongs to one task. Within a disposition every task
 * keeps the same residual share, so the story's per-line maths (state after a wave,
 * the Scenario Explorer) gives the same answer on the merged lines.
 */
function mergeLedger(tasks: GccValueCase['tasks']): SimpleLedgerGroup[] {
  const groups = new Map<string, SimpleLedgerGroup>()
  for (const l of tasks) {
    const key = [l.functionScope, l.capabilityName, l.ownership, l.disposition, l.transformWave, l.migrationWave].join('|')
    let g = groups.get(key)
    if (!g) {
      g = {
        capabilityName: l.capabilityName, functionScope: l.functionScope, ownership: l.ownership,
        transformWave: l.transformWave, migrationWave: l.migrationWave,
        baselineFte: 0, aiReleasedFte: 0, residualFte: 0,
        currentCostUSD: 0, aiSavingUSD: 0, locationSavingUSD: 0, totalSavingUSD: 0, endStateCostUSD: 0,
      }
      groups.set(key, g)
    }
    for (const k of FTES) g[k] = round(g[k] + (l[k] || 0), 4)
    for (const k of FIGURES) g[k] += l[k] || 0
  }
  return [...groups.values()]
}

/**
 * The data the Simple (client) export embeds (WI 2822): only what its tabs draw.
 * Nothing behind the Disposition, Sourcing Model, Sequencing or Value Case tabs is
 * kept: no task register (counts only), no sourcing model or rationale, no sequencing
 * beyond the per-wave FTE, no pay rates or assumptions, no per-task cost lines and
 * no per-role labour cost. The Work View's task lines (WI 2882) carry no cost or rate.
 */
export function toSimpleGccData(gcc: Omit<GccDashboardData, 'apiTrace'>) {
  const register = gcc.taskRegister?.taskRegister ?? []
  const vc = gcc.valueCase
  return {
    scope: { procs: register.length, tasks: register.reduce((n, p) => n + (p.tasks ?? []).length, 0) },
    taggedTasks: { taggedTasks: (gcc.taggedTasks?.taggedTasks ?? []).map(t => ({ disposition: t.disposition })) },
    sequencing: gcc.sequencing?.waveFlow ? { waveFlow: gcc.sequencing.waveFlow } : undefined,
    gccPopulation: simplePopulation(gcc.gccPopulation),
    retainedPopulation: simplePopulation(gcc.retainedPopulation),
    combined: simplePopulation(gcc.combined),
    valueCase: vc
      ? {
          capabilities: vc.capabilities.map(({ sourcingSplit: _split, ...c }) => c),
          totals: vc.totals,
          waves: vc.waves,
          tasks: mergeLedger(vc.tasks ?? []),
        }
      : undefined,
    agentBuilds: gccAgentBuilds(gcc),
    // Task-level lines of the Work View (WI 2882): FTE, disposition, ownership × location and waves only
    workView: gccWorkView(gcc),
    // Role Evolution Sankey (WI 2884): role names, FTE, tags and skills only
    roleEvolution: gccRoleEvolution(gcc),
  }
}
